import React from "react";
import { Popover, Button, Typography, Grid } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface ContextualHelpProps {
  /** i18n key for the title (from guide namespace) */
  titleKey: string;
  /** i18n key for the content (from guide namespace) */
  contentKey: string;
  /** Additional content to display after the main content */
  extra?: React.ReactNode;
  /** Icon size */
  size?: "small" | "default";
  /** Placement of the popover */
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight";
}

/**
 * ContextualHelp - Inline help icon with popover
 * Use next to form fields or UI elements that need explanation
 */
export const ContextualHelp: React.FC<ContextualHelpProps> = ({
  titleKey,
  contentKey,
  extra,
  size = "default",
  placement = "top",
}) => {
  const { t } = useTranslation("guide");
  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const iconSize = size === "small" ? 14 : 16;

  const content = (
    <div style={{ maxWidth: isMobile ? 240 : 300 }}>
      <Text strong style={{ display: "block", marginBottom: 8 }}>
        {t(titleKey)}
      </Text>
      <Paragraph
        style={{ marginBottom: extra ? 12 : 0 }}
        type="secondary"
        ellipsis={{ rows: 5, expandable: true }}
      >
        {t(contentKey)}
      </Paragraph>
      {extra}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger={isMobile ? "click" : "hover"}
      placement={placement}
    >
      <Button
        type="text"
        size="small"
        icon={
          <QuestionCircleOutlined
            style={{ fontSize: iconSize, color: "#8c8c8c" }}
          />
        }
        style={{
          padding: 0,
          width: iconSize + 8,
          height: iconSize + 8,
          minWidth: iconSize + 8,
        }}
        aria-label={t(titleKey)}
      />
    </Popover>
  );
};

/**
 * Simplified version for form field labels
 */
interface FieldHelpProps {
  /** Help topic key (e.g., "lifecycle", "workflow") */
  topic: "lifecycle" | "workflow" | "import" | "relationships" | "columns";
}

export const FieldHelp: React.FC<FieldHelpProps> = ({ topic }) => {
  return (
    <ContextualHelp
      titleKey={`contextualHelp.${topic}.title`}
      contentKey={`contextualHelp.${topic}.content`}
      size="small"
    />
  );
};
